import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getQuestionAnalytics, getClassrooms, getStudentObjectivesPerformance, getObjectiveStrugglingStudents } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Brain, AlertTriangle, Target, TrendingDown, 
  CheckCircle, XCircle, BarChart3, BookOpen, Users, User
} from 'lucide-react';

const QuestionAnalytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedClassroom, setSelectedClassroom] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [studentModal, setStudentModal] = useState({ open: false, data: null, loading: false });
  const [objectiveModal, setObjectiveModal] = useState({ open: false, data: null, loading: false });

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const res = await getClassrooms();
        setClassrooms(res.data);
      } catch (error) {
        console.error('Failed to load classrooms');
      }
    };
    fetchClassrooms();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const classroomId = selectedClassroom === 'all' ? null : selectedClassroom;
        const res = await getQuestionAnalytics(classroomId);
        setAnalytics(res.data);
      } catch (error) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [selectedClassroom]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'hard': return 'text-neon-red bg-neon-red/20 border-neon-red/30';
      case 'medium': return 'text-neon-yellow bg-neon-yellow/20 border-neon-yellow/30';
      case 'easy': return 'text-neon-green bg-neon-green/20 border-neon-green/30';
      default: return 'text-text-muted bg-white/10';
    }
  };

  const getCorrectRateColor = (rate) => {
    if (rate < 50) return 'text-neon-red';
    if (rate < 75) return 'text-neon-yellow';
    return 'text-neon-green';
  };

  // Handle click on student name
  const handleStudentClick = async (studentId, studentName) => {
    setStudentModal({ open: true, data: null, loading: true, studentName });
    try {
      const classroomId = selectedClassroom === 'all' ? null : selectedClassroom;
      const res = await getStudentObjectivesPerformance(studentId, classroomId);
      setStudentModal({ open: true, data: res.data, loading: false, studentName });
    } catch (error) {
      toast.error('Failed to load student data');
      setStudentModal({ open: false, data: null, loading: false });
    }
  };

  // Handle click on objective
  const handleObjectiveClick = async (objectiveCode, objectiveDescription) => {
    setObjectiveModal({ open: true, data: null, loading: true, objectiveCode, objectiveDescription });
    try {
      const classroomId = selectedClassroom === 'all' ? null : selectedClassroom;
      const res = await getObjectiveStrugglingStudents(objectiveCode, classroomId);
      setObjectiveModal({ open: true, data: res.data, loading: false, objectiveCode, objectiveDescription });
    } catch (error) {
      toast.error('Failed to load objective data');
      setObjectiveModal({ open: false, data: null, loading: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/teacher')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold flex items-center gap-2">
                <Brain className="w-6 h-6 text-primary" />
                Question Analytics
              </h1>
              <p className="text-text-secondary text-sm">Identify concepts students struggle with</p>
            </div>
          </div>
          
          {/* Classroom Filter */}
          <Select value={selectedClassroom} onValueChange={setSelectedClassroom}>
            <SelectTrigger className="w-48 bg-background-paper border-white/10" data-testid="classroom-filter">
              <SelectValue placeholder="All Classrooms" />
            </SelectTrigger>
            <SelectContent className="bg-background-paper border-white/10">
              <SelectItem value="all">All Classrooms</SelectItem>
              {classrooms.map((c) => (
                <SelectItem key={c.classroom_id} value={c.classroom_id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-glass p-5" data-testid="stat-total-questions">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Questions</p>
                <p className="font-heading text-2xl font-bold">{analytics?.summary?.total_questions || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-5" data-testid="stat-avg-correct">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-neon-green/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-neon-green" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Avg Correct</p>
                <p className={`font-heading text-2xl font-bold ${getCorrectRateColor(analytics?.summary?.avg_correct_rate)}`}>
                  {analytics?.summary?.avg_correct_rate || 0}%
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-5" data-testid="stat-hard-questions">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-neon-red/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-neon-red" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Hard Questions</p>
                <p className="font-heading text-2xl font-bold text-neon-red">
                  {analytics?.summary?.hard_questions || 0}
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-5" data-testid="stat-objectives-focus">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-neon-yellow/20 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-neon-yellow" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Needs Focus</p>
                <p className="font-heading text-2xl font-bold text-neon-yellow">
                  {analytics?.summary?.objectives_needing_focus || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-3 flex-wrap">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'btn-primary' : ''}
            data-testid="tab-overview"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </Button>
          <Button
            variant={activeTab === 'questions' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('questions')}
            className={activeTab === 'questions' ? 'btn-primary' : ''}
            data-testid="tab-questions"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Struggling Questions
          </Button>
          <Button
            variant={activeTab === 'objectives' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('objectives')}
            className={activeTab === 'objectives' ? 'btn-primary' : ''}
            data-testid="tab-objectives"
          >
            <Target className="w-4 h-4 mr-2" />
            By Objective
          </Button>
          <Button
            variant={activeTab === 'students' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('students')}
            className={activeTab === 'students' ? 'btn-primary' : ''}
            data-testid="tab-students"
          >
            <Users className="w-4 h-4 mr-2" />
            By Student
          </Button>
          <Button
            variant={activeTab === 'mistakes' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('mistakes')}
            className={activeTab === 'mistakes' ? 'btn-primary' : ''}
            data-testid="tab-mistakes"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Common Mistakes
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hardest Questions Preview */}
            <Card className="card-glass p-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-neon-red" />
                Top 5 Hardest Questions
              </h3>
              {analytics?.struggling_questions?.slice(0, 5).length > 0 ? (
                <div className="space-y-3">
                  {analytics.struggling_questions.slice(0, 5).map((q, idx) => (
                    <div key={idx} className="bg-black/30 rounded-lg p-3 border border-white/10">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-sm text-text-primary mb-1">{q.question_text}</p>
                          <p className="text-xs text-text-muted">
                            {q.exercise_title} • {q.objective_code}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs border ${getDifficultyColor(q.difficulty)}`}>
                            {q.correct_rate}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-text-muted text-center py-8">No question data yet</p>
              )}
            </Card>

            {/* Objectives Needing Focus */}
            <Card className="card-glass p-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-neon-yellow" />
                Objectives Needing Focus
              </h3>
              {analytics?.by_objective?.filter(o => o.needs_focus).length > 0 ? (
                <div className="space-y-3">
                  {analytics.by_objective.filter(o => o.needs_focus).slice(0, 5).map((obj, idx) => (
                    <div key={idx} className="bg-black/30 rounded-lg p-3 border border-white/10">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="font-mono text-primary text-sm mb-1">{obj.code}</p>
                          <p className="text-xs text-text-muted">{obj.description}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono font-bold ${getCorrectRateColor(obj.correct_rate)}`}>
                            {obj.correct_rate}%
                          </p>
                          <p className="text-xs text-text-muted">{obj.unique_students} students</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-10 h-10 text-neon-green mx-auto mb-2" />
                  <p className="text-neon-green">All objectives above 70%!</p>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'questions' && (
          <Card className="card-glass overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold">Questions by Difficulty</h3>
              <p className="text-text-muted text-sm">Sorted by lowest correct rate</p>
            </div>
            {analytics?.struggling_questions?.length > 0 ? (
              <div className="divide-y divide-white/5">
                {analytics.struggling_questions.map((q, idx) => (
                  <div key={idx} className="p-4 hover:bg-white/5 transition-colors" data-testid={`question-row-${idx}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs border ${getDifficultyColor(q.difficulty)}`}>
                            {q.difficulty.toUpperCase()}
                          </span>
                          <span className="text-xs text-text-muted">{q.objective_code}</span>
                        </div>
                        <p className="text-text-primary mb-2">{q.question_text}</p>
                        <p className="text-xs text-text-muted mb-2">
                          From: <span className="text-primary">{q.exercise_title}</span>
                        </p>
                        
                        {/* Wrong answers breakdown */}
                        {q.common_wrong_answers?.length > 0 && (
                          <div className="mt-3 bg-neon-red/10 rounded p-2 border border-neon-red/20">
                            <p className="text-xs text-neon-red mb-1 font-medium">Common wrong answers:</p>
                            <div className="flex flex-wrap gap-2">
                              {q.common_wrong_answers.map((wa, waIdx) => (
                                <span key={waIdx} className="text-xs bg-neon-red/20 px-2 py-1 rounded">
                                  &quot;{wa.answer}&quot; ({wa.count}x)
                                </span>
                              ))}
                            </div>
                            <p className="text-xs text-neon-green mt-2">
                              Correct answer: <span className="font-bold">{q.correct_answer}</span>
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="text-right min-w-[100px]">
                        <p className={`font-mono text-2xl font-bold ${getCorrectRateColor(q.correct_rate)}`}>
                          {q.correct_rate}%
                        </p>
                        <p className="text-xs text-text-muted">
                          {q.correct_count}/{q.total_attempts} correct
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <BookOpen className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">No quiz data available yet</p>
                <p className="text-text-muted text-sm">Students need to complete exercises first</p>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'objectives' && (
          <Card className="card-glass overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="font-heading text-lg font-bold">Performance by ELA Objective</h3>
              <p className="text-text-muted text-sm">Click any objective to see struggling students</p>
            </div>
            {analytics?.by_objective?.length > 0 ? (
              <div className="divide-y divide-white/5">
                {analytics.by_objective.map((obj, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 hover:bg-white/5 transition-colors cursor-pointer" 
                    data-testid={`objective-row-${idx}`}
                    onClick={() => handleObjectiveClick(obj.code, obj.description)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-primary font-bold">{obj.code}</span>
                          {obj.needs_focus && (
                            <span className="px-2 py-0.5 rounded text-xs bg-neon-yellow/20 text-neon-yellow border border-neon-yellow/30">
                              Needs Focus
                            </span>
                          )}
                        </div>
                        <p className="text-text-secondary text-sm">{obj.description}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-text-muted text-xs">Students</p>
                          <p className="font-mono font-bold">{obj.unique_students}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-text-muted text-xs">Questions</p>
                          <p className="font-mono font-bold">{obj.total_questions}</p>
                        </div>
                        <div className="text-center min-w-[80px]">
                          <p className="text-text-muted text-xs">Correct Rate</p>
                          <p className={`font-mono text-xl font-bold ${getCorrectRateColor(obj.correct_rate)}`}>
                            {obj.correct_rate}%
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 bg-black/30 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          obj.correct_rate >= 75 ? 'bg-neon-green' : 
                          obj.correct_rate >= 50 ? 'bg-neon-yellow' : 'bg-neon-red'
                        }`}
                        style={{ width: `${obj.correct_rate}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Target className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">No objective data available yet</p>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'mistakes' && (
          <Card className="card-glass overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="font-heading text-lg font-bold">Most Common Mistakes</h3>
              <p className="text-text-muted text-sm">Frequent wrong answers on difficult questions</p>
            </div>
            {analytics?.common_mistakes?.length > 0 ? (
              <div className="divide-y divide-white/5">
                {analytics.common_mistakes.map((mistake, idx) => (
                  <div key={idx} className="p-4 hover:bg-white/5 transition-colors" data-testid={`mistake-row-${idx}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-text-primary text-sm mb-2">{mistake.question}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-neon-red flex items-center gap-1">
                            <XCircle className="w-4 h-4" />
                            Chose: <span className="font-bold">{mistake.wrong_answer}</span>
                          </span>
                          <span className="text-neon-green flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Correct: <span className="font-bold">{mistake.correct_answer}</span>
                          </span>
                        </div>
                        <p className="text-xs text-text-muted mt-1">{mistake.objective_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-2xl font-bold text-neon-red">{mistake.times_chosen}</p>
                        <p className="text-xs text-text-muted">times</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <CheckCircle className="w-12 h-12 text-neon-green mx-auto mb-4" />
                <p className="text-neon-green">No common mistakes found</p>
                <p className="text-text-muted text-sm">Students are doing well!</p>
              </div>
            )}
          </Card>
        )}

        {/* By Student Tab */}
        {activeTab === 'students' && (
          <Card className="card-glass overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="font-heading text-lg font-bold">Quiz Performance by Student</h3>
              <p className="text-text-muted text-sm">Click any student to see their objectives breakdown</p>
            </div>
            {analytics?.by_student?.length > 0 ? (
              <div className="divide-y divide-white/5">
                {analytics.by_student.map((student, idx) => (
                  <div 
                    key={student.student_id || idx} 
                    className="p-4 hover:bg-white/5 transition-colors cursor-pointer" 
                    data-testid={`student-row-${idx}`}
                    onClick={() => handleStudentClick(student.student_id, student.name)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{student.name}</p>
                          <p className="text-text-muted text-xs">{student.exercises_completed} exercises completed</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-text-muted text-xs">Objectives</p>
                          <p className="font-mono font-bold">{student.objectives_count || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-text-muted text-xs">Mastered</p>
                          <p className="font-mono font-bold text-neon-green">{student.mastered_count || 0}</p>
                        </div>
                        <div className="text-center min-w-[80px]">
                          <p className="text-text-muted text-xs">Avg Quiz</p>
                          <p className={`font-mono text-xl font-bold ${getCorrectRateColor(student.avg_quiz_score || 0)}`}>
                            {student.avg_quiz_score || 0}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">No student data available yet</p>
                <p className="text-text-muted text-sm">Students need to complete exercises first</p>
              </div>
            )}
          </Card>
        )}
      </main>

      {/* Student Objectives Modal */}
      <Dialog open={studentModal.open} onOpenChange={(open) => !open && setStudentModal({ ...studentModal, open: false })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              {studentModal.studentName}'s Objectives Performance
            </DialogTitle>
          </DialogHeader>
          
          {studentModal.loading ? (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-text-muted">Loading...</p>
            </div>
          ) : studentModal.data ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-black/30 rounded-lg p-3 text-center">
                  <p className="text-text-muted text-xs">Objectives</p>
                  <p className="font-heading text-xl font-bold text-primary">{studentModal.data.summary?.total_objectives || 0}</p>
                </div>
                <div className="bg-black/30 rounded-lg p-3 text-center">
                  <p className="text-text-muted text-xs">Mastered</p>
                  <p className="font-heading text-xl font-bold text-neon-green">{studentModal.data.summary?.mastered || 0}</p>
                </div>
                <div className="bg-black/30 rounded-lg p-3 text-center">
                  <p className="text-text-muted text-xs">Needs Work</p>
                  <p className="font-heading text-xl font-bold text-neon-yellow">{studentModal.data.summary?.needs_work || 0}</p>
                </div>
                <div className="bg-black/30 rounded-lg p-3 text-center">
                  <p className="text-text-muted text-xs">Avg Quiz</p>
                  <p className={`font-heading text-xl font-bold ${getCorrectRateColor(studentModal.data.summary?.avg_quiz_overall || 0)}`}>
                    {studentModal.data.summary?.avg_quiz_overall || 0}%
                  </p>
                </div>
              </div>
              
              {/* Objectives List */}
              <div className="space-y-2">
                <h4 className="font-heading text-sm text-text-muted">Objectives (sorted by performance)</h4>
                {studentModal.data.objectives?.map((obj, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${obj.mastered ? 'bg-neon-green/5 border-neon-green/20' : 'bg-neon-yellow/5 border-neon-yellow/20'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-primary text-sm font-bold">{obj.code}</span>
                          {obj.mastered ? (
                            <CheckCircle className="w-4 h-4 text-neon-green" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-neon-yellow" />
                          )}
                        </div>
                        <p className="text-text-muted text-xs">{obj.description}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-text-muted text-xs">Practiced</p>
                          <p className="font-mono font-bold">{obj.times_practiced}x</p>
                        </div>
                        <div className="text-center">
                          <p className="text-text-muted text-xs">Quiz Avg</p>
                          <p className={`font-mono font-bold ${getCorrectRateColor(obj.avg_quiz_score)}`}>
                            {obj.avg_quiz_score}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-center text-text-muted py-8">No data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Objective Struggling Students Modal */}
      <Dialog open={objectiveModal.open} onOpenChange={(open) => !open && setObjectiveModal({ ...objectiveModal, open: false })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              {objectiveModal.objectiveCode} - Students Below 80%
            </DialogTitle>
          </DialogHeader>
          
          {objectiveModal.loading ? (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-text-muted">Loading...</p>
            </div>
          ) : objectiveModal.data ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-black/30 rounded-lg p-4">
                <p className="text-text-muted text-sm mb-2">{objectiveModal.data.objective_description}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-neon-red">
                    <strong>{objectiveModal.data.summary?.struggling_count || 0}</strong> students below 80%
                  </span>
                  <span className="text-neon-green">
                    <strong>{objectiveModal.data.summary?.mastered_count || 0}</strong> students mastered
                  </span>
                </div>
              </div>
              
              {/* Struggling Students List */}
              {objectiveModal.data.struggling_students?.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="font-heading text-sm text-text-muted">Students Needing Help (sorted by lowest score)</h4>
                  {objectiveModal.data.struggling_students.map((student, idx) => (
                    <div 
                      key={student.student_id} 
                      className="p-3 rounded-lg bg-neon-red/5 border border-neon-red/20 cursor-pointer hover:bg-neon-red/10"
                      onClick={() => {
                        setObjectiveModal({ ...objectiveModal, open: false });
                        handleStudentClick(student.student_id, student.name);
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-neon-red/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-neon-red" />
                          </div>
                          <div>
                            <p className="font-medium">{student.name}</p>
                            <p className="text-text-muted text-xs">{student.attempts} attempt{student.attempts !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-neon-red text-lg">{student.avg_quiz_score}%</p>
                          <p className="text-text-muted text-xs">quiz avg</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-neon-green mx-auto mb-2" />
                  <p className="text-neon-green">All students have mastered this objective!</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-text-muted py-8">No data available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionAnalytics;
